//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";

// web settings working hours imports
import { IProps, IState, IContainer } from "./WebSettingsWorkingHours.d";
import { styles } from "./WebSettingsWorkingHours.styles";
import { fragments } from "./WebSettingsWorkingHours.gql";

import {
  WebSettingsContext,
  WebSettingsSections,
  HoursAndPreferencesSections,
  TeamsSections,
  createSettingsViewId,
} from "../../web-settings";

// other internals
import {
  TimeZonePicker,
  TimeZoneDisplayMode,
} from "@clockwise/web-commons/src/ui/time-zone-picker";
import { CalloutBox } from "@components/callout-box";
import {
  CopyButtonStyle,
  Day,
  getAreWorkingHoursForDaySplit,
  getDaySettingsFromWorkingHours,
  getNumSplitHoursForDay,
  IWorkingHours,
  SplitMeetingHours,
  SplitWorkingHours,
} from "@clockwise/web-commons/src/ui/working-hours";
import {
  track,
  trackFSEvent,
  TrackingEvents,
} from "@clockwise/web-commons/src/util/analytics.util";

// mutations
import { updateWorkingHours } from "@mutations";

// util
import {
  meetingAndWorkingHoursAreDifferent,
  workingHoursVaryDaily,
  getMeetingHoursOutsideWorkingHours,
} from "@util/working-hours.util";
import { getRenderTimeZone, setRenderTimeZone } from "@util/time-zone.util";
import {
  isNoMeetingDayEnabledForUserFromTeam,
  getNoMeetingDayOfWeekFromTeam,
} from "@util/team-settings.util";

// constants
import { daysOfWeek } from "@constants/date.constant";

// state
import { IReduxState } from "@reducers/root.reducer";
import { snackBarOpen } from "@actions/snack-bar.actions";

// material-ui
import { Warning } from "@material-ui/icons";
import { Button, Checkbox, FormControl, FormControlLabel, Typography } from "@material-ui/core";
import withStyles from "@material-ui/core/styles/withStyles";

const AUTOSAVE_TIMEOUT = 3000;

//////////////////
// COMPONENT
//////////////////
export class WebSettingsWorkingHoursCmpt extends React.Component<IProps, IState> {
  static contextType = WebSettingsContext;
  declare context: React.ContextType<typeof WebSettingsContext>;

  private saveTimeout: ReturnType<typeof setTimeout>;

  // ~-~-~-~-~-~-~-
  // Setup
  // ~-~-~-~-~-~-~-
  private getStateFromProps = (): IState => {
    const { userMeetingHours, userWorkingHours } = this.props.org;
    const showMeetingHours = meetingAndWorkingHoursAreDifferent(
      userMeetingHours.workingHours,
      userWorkingHours.workingHours,
    );
    const workingHoursSameDaily =
      !workingHoursVaryDaily(userWorkingHours.workingHours) &&
      !workingHoursVaryDaily(userMeetingHours.workingHours);

    let timeZone = userWorkingHours.workingHours.timeZone;
    if (!timeZone || !userWorkingHours.hasBeenSet) {
      timeZone = getRenderTimeZone();
    }

    return {
      showMeetingHours,
      timeZone,
      workingHoursSameDaily,
      workingSaving: false,
      workingHoursErrorMessage: "",
      meetingSaving: false,
      meetingHoursErrorMessage: "",
      meetingAndWorkingHoursRangeErrorDays: [],
      meetingHours: userMeetingHours.workingHours,
      workingHours: userWorkingHours.workingHours,
    };
  };

  constructor(props: IProps) {
    super(props);
    this.state = this.getStateFromProps();
  }

  public componentWillUnmount() {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }
  }

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private validateMeetingAndWorkingHoursRanges = (
    meetingHours: ISchema.IWorkingHours,
    workingHours: ISchema.IWorkingHours,
  ) => {
    const meetingAndWorkingHoursRangeErrorDays = getMeetingHoursOutsideWorkingHours(
      meetingHours,
      workingHours,
      !this.state.showMeetingHours,
    );
    this.setState({ meetingAndWorkingHoursRangeErrorDays });

    const isValid =
      !this.state.workingHoursErrorMessage &&
      !this.state.meetingHoursErrorMessage &&
      meetingAndWorkingHoursRangeErrorDays.length === 0;

    if (this.props.onValidateWorkingAndMeetingHours) {
      this.props.onValidateWorkingAndMeetingHours(!isValid);
    }

    return isValid;
  };

  // ~-~-~-~-~-~-~-
  // Event Handling
  // ~-~-~-~-~-~-~-

  // button actions
  private saveHours = () => {
    // only save meeting hours if they're showing
    this.setState(
      { meetingSaving: this.state.showMeetingHours, workingSaving: true },
      this.saveWorkingAndMeetingHours, // Wait until meetingSaving and workingSaving set
    );
    setRenderTimeZone(this.state.timeZone);
  };

  private onCheckMeetingShowing = (
    _event: React.ChangeEvent<HTMLInputElement>,
    isChecked: boolean,
  ) => {
    const meetingAndWorkingHoursRangeErrorDays = getMeetingHoursOutsideWorkingHours(
      this.state.meetingHours,
      this.state.workingHours,
      isChecked,
    );

    this.setState({ meetingAndWorkingHoursRangeErrorDays, showMeetingHours: !isChecked }, () => {
      this.validateMeetingAndWorkingHoursRanges(this.state.meetingHours, this.state.workingHours);
    });
  };

  // Saving Callbacks
  private doneSavingMeetingHours = () => {
    this.setState({ meetingSaving: false }, this.maybeSaveCallback);
  };

  private doneSavingWorkingHours = () => {
    this.setState({ workingSaving: false }, this.maybeSaveCallback);
  };

  private maybeSaveCallback = () => {
    if (this.state.meetingSaving || this.state.workingSaving) {
      return;
    }

    // callback
    if (this.props.onSave) {
      this.props.onSave();
    }
  };

  private saveWorkingAndMeetingHours = () => {
    const { workingHours, meetingHours, meetingSaving, timeZone } = this.state;

    if (this.validateMeetingAndWorkingHoursRanges(meetingHours, workingHours)) {
      // Update working hours
      updateWorkingHours(
        this.props.relay.environment,
        {
          workingHours: Object.assign({}, workingHours, {
            type: ISchema.WorkingHoursTypeEnum.Base,
            timeZone,
          }),
          saveMeetingAndWorkingHours: !this.state.showMeetingHours,
        },
        this.saveCallback.bind(null, `Your working hours were updated.`),
        this.saveCallback.bind(null, `Whoops, there was a problem saving your working hours.`),
      );

      this.doneSavingWorkingHours();

      if (meetingSaving) {
        // Update meeting hours
        updateWorkingHours(
          this.props.relay.environment,
          {
            workingHours: Object.assign({}, meetingHours, {
              type: ISchema.WorkingHoursTypeEnum.Meeting,
              timeZone,
            }),
            saveMeetingAndWorkingHours: !this.state.showMeetingHours,
          },
          this.saveCallback.bind(null, `Your meeting hours were updated.`),
          this.saveCallback.bind(null, `Whoops, there was a problem saving your meeting hours.`),
        );

        this.doneSavingMeetingHours();
      }
    }
  };

  private saveCallback = (msg: string) => {
    if (!this.props.isOnboarding) {
      this.props.snackBarOpen(msg);
    }
  };

  private handleChange = (
    meetingHours: ISchema.IWorkingHours,
    workingHours: ISchema.IWorkingHours,
  ) => {
    const { allowAutoSave } = this.props;
    const isValid = this.validateMeetingAndWorkingHoursRanges(meetingHours, workingHours);
    this.setState({ meetingHours, workingHours });

    // if we dont allow autosave, just return
    if (!allowAutoSave) {
      return;
    }

    // always clear to debounce the timeout, even if invalid
    clearTimeout(this.saveTimeout);

    // if valid, set new autosave interval
    if (isValid) {
      this.saveTimeout = setTimeout(() => {
        this.saveHours();
      }, AUTOSAVE_TIMEOUT);
    }
  };

  private onTimeZoneChange = (timeZone: string) => {
    this.setState({ timeZone });
    this.handleChange(this.state.meetingHours, this.state.workingHours); // To set autosave timeout
  };

  private handleMeetingHoursChange = (
    meetingHours: ISchema.IWorkingHours,
    workingHours: ISchema.IWorkingHours,
    updatedDayOfWeek?: Day,
  ) => {
    trackFSEvent(TrackingEvents.SETTINGS.UPDATE_SPLIT_MEETING_HOURS);

    const daySettings =
      updatedDayOfWeek &&
      getDaySettingsFromWorkingHours((meetingHours as unknown) as IWorkingHours, updatedDayOfWeek);

    const updatePayload = {
      dayOfWeek: updatedDayOfWeek,
      isOn: !!daySettings,
      nSplits: daySettings ? getNumSplitHoursForDay(daySettings) : 0,
      isSplit: daySettings ? getAreWorkingHoursForDaySplit(daySettings) : false,
    };
    track(TrackingEvents.SETTINGS.UPDATE_SPLIT_MEETING_HOURS, updatePayload);

    this.handleChange(meetingHours, workingHours);
  };

  private handleWorkingHoursChange = (
    workingHours: ISchema.IWorkingHours,
    updatedDayOfWeek?: Day,
  ) => {
    let meetingHours = Object.assign({}, this.state.meetingHours);
    if (!this.state.showMeetingHours) {
      // just set to working hours because they are the same
      meetingHours = { ...workingHours };
    } else {
      // set to meeting hours state
      const mhDaySettings: ISchema.IDaySetting[] = [];
      daysOfWeek.forEach((day) => {
        const mhDay = meetingHours.daySettings.find((mh) => mh.day === day);
        const whDay = workingHours.daySettings.find((wh) => wh.day === day);
        if (!whDay) {
          // but remove any day that is not in working hours
          return;
        }
        if (!mhDay && !!whDay) {
          // if no meeting hours, use working hours
          return mhDaySettings.push(whDay);
        }
        if (mhDay) {
          // otherwise set to meeting hours state
          return mhDaySettings.push(mhDay);
        }
        // this return placates TS; without it
        // not all code paths return a value
        return;
      });
      meetingHours.daySettings = mhDaySettings;
    }

    trackFSEvent(TrackingEvents.SETTINGS.UPDATE_SPLIT_WORKING_HOURS);

    const daySettings =
      updatedDayOfWeek &&
      getDaySettingsFromWorkingHours((workingHours as unknown) as IWorkingHours, updatedDayOfWeek);

    const updatePayload = {
      dayOfWeek: updatedDayOfWeek,
      isOn: !!daySettings,
      nSplits: daySettings ? getNumSplitHoursForDay(daySettings) : 0,
      isSplit: daySettings ? getAreWorkingHoursForDaySplit(daySettings) : false,
    };

    track(TrackingEvents.SETTINGS.UPDATE_SPLIT_WORKING_HOURS, updatePayload);

    // we track meeting hours updates when
    // meeting hours equal working hours
    if (!this.state.showMeetingHours) {
      track(TrackingEvents.SETTINGS.UPDATE_SPLIT_MEETING_HOURS, updatePayload);
    }

    this.handleChange(meetingHours, workingHours);
  };

  // ~-~-~-~-~-~-~-
  // Rendering
  // ~-~-~-~-~-~-~-
  private maybeRenderTimeZonePicker() {
    if (this.props.hideTimeZone) {
      return "";
    }

    return (
      <div cw-id={`web-settings-${HoursAndPreferencesSections.TimeZone}`} className="cw-mb-16">
        <Typography variant="h3" className="cw-mb-4">
          What time zone are you in?
        </Typography>
        <TimeZonePicker
          timeZoneDisplayMode={TimeZoneDisplayMode.JustPicker}
          onChange={this.onTimeZoneChange}
          value={this.state.timeZone}
          className="cw-mb-6 cw-mt-3 cw-max-w-[450px]"
        />
      </div>
    );
  }

  private renderWorkingHours() {
    const { classes } = this.props;

    return (
      <div cw-id={`web-settings-${HoursAndPreferencesSections.WorkingHours}`} className="cw-mb-16">
        <Typography variant="h3" className="cw-mb-4">
          When do you start and stop working each day?
        </Typography>
        <Typography variant="subtitle1" className="cw-mb-6">
          We'll use this information to analyze how many hours of Focus Time you have.
        </Typography>
        {this.props.org.primaryTeam &&
        isNoMeetingDayEnabledForUserFromTeam(this.props.org.primaryTeam) ? (
          <CalloutBox warning className={classes.calloutContainer}>
            <Warning className={classes.calloutIcon} />
            <span className={classes.calloutLabel}>
              Clockwise will avoid scheduling meetings on your team’s no-meeting day on{" "}
              {getNoMeetingDayOfWeekFromTeam(this.props.org.primaryTeam)}
            </span>
            <Button
              variant="text"
              className={classes.manageBtn}
              onClick={() => {
                const teamId =
                  this.props.org.primaryTeam.__typename === "Team"
                    ? this.props.org.primaryTeam.teamId
                    : undefined;

                this.context.onChangeSettingsView(
                  createSettingsViewId(WebSettingsSections.Teams, TeamsSections.Team, teamId),
                );
              }}
            >
              Manage
            </Button>
          </CalloutBox>
        ) : (
          ""
        )}
        <div className="cw-mt-2.5 cw-mb-11">
          <SplitWorkingHours
            // FIXME: Don't cast
            workingHours={(this.state.workingHours as unknown) as IWorkingHours}
            copyButtonStyle={CopyButtonStyle.Standard}
            onUpdateWorkingHours={
              (this.handleWorkingHoursChange as unknown) as (
                workingHours: IWorkingHours,
                day: Day,
              ) => void
            }
          />
        </div>
      </div>
    );
  }

  private renderMeetingHours = () => {
    return (
      <div cw-id={`web-settings-${HoursAndPreferencesSections.MeetingHours}`} className="cw-mb-16">
        <Typography variant="h3" className="cw-mb-4">
          When are you available to meet with others?
        </Typography>
        <Typography variant="subtitle1" className="cw-mb-6">
          We'll use this to protect time during your workday where you'd prefer to avoid meeting.
          Meeting hours must be within your working hours.
        </Typography>
        <FormControl fullWidth>
          <FormControlLabel
            control={<Checkbox color="primary" checked={!this.state.showMeetingHours} />}
            onChange={this.onCheckMeetingShowing}
            label="My meeting hours are the same as my working hours"
            labelPlacement="end"
          />
        </FormControl>
        {/* note: it's important that the working hours are removed from the render
          when the toggle is off — this forces it to re-initialize */}
        {this.state.showMeetingHours && (
          <div className="cw-mt-2.5 cw-mb-11">
            <SplitMeetingHours
              // FIXME: Fix types for workingHours and meetingHours
              workingHours={this.state.workingHours as any}
              meetingHours={this.state.meetingHours as any}
              copyButtonStyle={CopyButtonStyle.Standard}
              onUpdateMeetingHours={
                ((meetingHours: ISchema.IWorkingHours, updatedDayOfWeek: Day) => {
                  this.handleMeetingHoursChange(
                    meetingHours,
                    this.state.workingHours,
                    updatedDayOfWeek,
                  );
                }) as any
              }
            />
          </div>
        )}
      </div>
    );
  };

  public render() {
    return (
      <>
        {this.renderWorkingHours()}
        {this.maybeRenderTimeZonePicker()}
        {this.renderMeetingHours()}
      </>
    );
  }
}

//////////////////
// WITH STYLES
//////////////////
export const WebSettingsWorkingHoursStyled = withStyles(styles)(WebSettingsWorkingHoursCmpt);

//////////////////
// REDUX
//////////////////
function mapStateToProps(_state: IReduxState, _ownProps: IContainer) {
  return {}; // add redux state here, if needed
}

const reduxContainer = connect(mapStateToProps, {
  snackBarOpen,
})(WebSettingsWorkingHoursStyled);

//////////////////
// RELAY
//////////////////
export const WebSettingsWorkingHours = createFragmentContainer(reduxContainer, fragments);
